import React from 'react';
import { useNavigate } from 'react-router-dom';

const whyChoose = [
  { icon: '🎯', title: 'Industry Expertise', desc: 'We speak your language. Our team has direct construction experience and understands site requirements, safety protocols, and trade qualifications.' },
  { icon: '⚡', title: 'Fast Response Times', desc: 'Urgent requirement? We can supply qualified candidates within 24 hours for many roles.' },
  { icon: '✅', title: 'Compliance Guaranteed', desc: 'Full right-to-work checks, CSCS verification, qualification checks, and safety inductions.' },
  { icon: '💰', title: 'Cost-Effective Solutions', desc: 'Competitive rates, transparent pricing, no hidden fees. Pay only for productive hours.' },
  { icon: '🤝', title: 'Dedicated Account Management', desc: 'A single point of contact who knows your business, sites, and requirements.' },
  { icon: '📊', title: 'Quality Candidates', desc: 'Thorough vetting process ensures only skilled, reliable professionals join your team.' },
];

const employerServices = [
  {
    title: 'On-Demand Staffing',
    icon: '📞',
    features: ['Call-out service for urgent cover', 'Same-day placements where possible', '24/7 emergency support line'],
  },
  {
    title: 'Planned Recruitment',
    icon: '📋',
    features: ['Project-specific team building', 'Phased recruitment programs', 'Graduate & apprentice schemes'],
  },
  {
    title: 'Managed Services',
    icon: '⚙️',
    features: ['Complete workforce solutions', 'Payroll management', 'Compliance administration', 'Performance monitoring'],
  },
  {
    title: 'Retained Search',
    icon: '🔍',
    features: ['Executive & senior appointments', 'Confidential headhunting', 'Market mapping', 'Succession planning'],
  },
];

const caseStudies = [
  {
    title: 'Major Housing Development',
    client: 'Regional Housebuilder',
    challenge: '40-plot development needed complete site team',
    solution: 'Supplied 12-person team including site manager, trades, and labourers',
    outcome: 'Project completed 2 weeks ahead of schedule',
  },
  {
    title: 'Emergency M&E Cover',
    client: 'Commercial Contractor',
    challenge: 'Electrician shortage threatened project deadline',
    solution: '3 qualified electricians supplied within 48 hours',
    outcome: 'Project kept on schedule, client relationship strengthened',
  },
  {
    title: 'Infrastructure Project',
    client: 'Civil Engineering Firm',
    challenge: 'Needed experienced setting-out engineer for 6-month contract',
    solution: 'Headhunted specialist from network',
    outcome: 'Engineer extended contract; client now uses us for all recruitment',
  },
];

export default function EmployersPage() {
  const navigate = useNavigate();

  return (
    <div>
      {/* Page Hero */}
      <section className="page-hero">
        <div className="container">
          <h1>For Employers</h1>
          <p>Partner with construction recruitment specialists who deliver results</p>
          <div className="breadcrumb">
            <a href="/" onClick={(e) => { e.preventDefault(); navigate('/'); }}>Home</a>
            <span>/</span>
            <span style={{ color: 'rgba(255,255,255,0.9)' }}>Employers</span>
          </div>
        </div>
      </section>

      {/* Intro */}
      <section className="section">
        <div className="container" style={{ textAlign: 'center', maxWidth: '800px' }}>
          <h2>Your Recruitment Partner</h2>
          <p style={{ fontSize: '1.15rem', lineHeight: '1.8' }}>
            Partner with construction recruitment specialists who understand your
            industry, your deadlines, and your quality standards. We supply
            pre-vetted, qualified professionals ready to contribute from day one.
          </p>
        </div>
      </section>

      {/* Why Choose */}
      <section className="section-alt">
        <div className="container">
          <div className="section-header">
            <h2>Why Companies Choose Horizon</h2>
          </div>
          <div className="grid grid-3">
            {whyChoose.map((item, i) => (
              <div className="card" key={i} style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '2.5rem', marginBottom: '15px' }}>{item.icon}</div>
                <h4>{item.title}</h4>
                <p>{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Employer Services */}
      <section className="section">
        <div className="container">
          <div className="section-header">
            <h2>Our Employer Services</h2>
            <p>Flexible solutions tailored to your recruitment needs.</p>
          </div>
          <div className="grid grid-4">
            {employerServices.map((s, i) => (
              <div className="card" key={i}>
                <div style={{ fontSize: '2rem', marginBottom: '15px' }}>{s.icon}</div>
                <h4>{s.title}</h4>
                <ul className="card-list">
                  {s.features.map((f, j) => (
                    <li key={j}>{f}</li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Client Process */}
      <section className="section-dark">
        <div className="container">
          <div className="section-header">
            <h2>Our Process</h2>
            <p style={{ color: 'rgba(255,255,255,0.8)' }}>From first call to placement — fast and efficient.</p>
          </div>
          <div className="process-steps">
            {[
              { num: '1', title: 'Discovery Call', desc: 'Understand your needs' },
              { num: '2', title: 'Specification', desc: 'Define requirements' },
              { num: '3', title: 'Search', desc: 'Activate our network' },
              { num: '4', title: 'Screen', desc: 'Verify & shortlist' },
              { num: '5', title: 'Interview', desc: 'Coordinate & assist' },
              { num: '6', title: 'Placement', desc: 'Smooth onboarding' },
              { num: '7', title: 'Follow-up', desc: 'Ongoing support' },
              { num: '8', title: 'Feedback', desc: 'Review & improve' },
            ].map((step, i) => (
              <div className="process-step" key={i}>
                <div className="process-step-number">{step.num}</div>
                <h4 style={{ color: 'var(--white)' }}>{step.title}</h4>
                <p style={{ color: 'rgba(255,255,255,0.7)' }}>{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Case Studies */}
      <section className="section">
        <div className="container">
          <div className="section-header">
            <h2>Case Studies</h2>
            <p>Real results for real construction projects.</p>
          </div>
          <div className="grid grid-3">
            {caseStudies.map((cs, i) => (
              <div className="case-study" key={i}>
                <h4>{cs.title}</h4>
                <p style={{ fontSize: '0.9rem', color: 'var(--gray-600)', marginBottom: '15px' }}>{cs.client}</p>
                <div className="case-study-detail" style={{ flexDirection: 'column' }}>
                  <div style={{ width: '100%' }}>
                    <strong>Challenge</strong>
                    <p style={{ fontSize: '0.9rem' }}>{cs.challenge}</p>
                  </div>
                  <div style={{ width: '100%' }}>
                    <strong>Solution</strong>
                    <p style={{ fontSize: '0.9rem' }}>{cs.solution}</p>
                  </div>
                  <div style={{ width: '100%' }}>
                    <strong>Outcome</strong>
                    <p style={{ fontSize: '0.9rem', color: 'var(--success)', fontWeight: 600 }}>{cs.outcome}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Request Staff Form */}
      <section className="section-alt">
        <div className="container" style={{ maxWidth: '800px' }}>
          <div className="section-header">
            <h2>Request Staff</h2>
            <p>Tell us what you need and we'll get back to you within 2 hours.</p>
          </div>
          <form onSubmit={(e) => { e.preventDefault(); alert('Thank you! Our team will contact you shortly.'); }}
            style={{ background: 'var(--white)', padding: '40px', borderRadius: 'var(--radius-lg)', boxShadow: 'var(--shadow-md)' }}>
            <div className="form-row">
              <div className="form-group">
                <label>Company Name *</label>
                <input type="text" className="form-control" placeholder="Your company name" required />
              </div>
              <div className="form-group">
                <label>Contact Person *</label>
                <input type="text" className="form-control" placeholder="Your name" required />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Email *</label>
                <input type="email" className="form-control" placeholder="your@company.com" required />
              </div>
              <div className="form-group">
                <label>Phone *</label>
                <input type="tel" className="form-control" placeholder="Phone number" required />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Number of Workers Needed</label>
                <input type="number" className="form-control" placeholder="e.g. 5" min="1" />
              </div>
              <div className="form-group">
                <label>Trade / Role Required *</label>
                <input type="text" className="form-control" placeholder="e.g. Electricians, Labourers" required />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Start Date</label>
                <input type="date" className="form-control" />
              </div>
              <div className="form-group">
                <label>Duration</label>
                <select className="form-control">
                  <option value="">Select...</option>
                  <option>Temporary</option>
                  <option>Permanent</option>
                  <option>Contract</option>
                </select>
              </div>
            </div>
            <div className="form-group">
              <label>Location</label>
              <input type="text" className="form-control" placeholder="Project location" />
            </div>
            <div className="form-group">
              <label>Additional Requirements</label>
              <textarea className="form-control" placeholder="Any specific requirements, certifications, or details..."></textarea>
            </div>
            <button type="submit" className="btn btn-primary btn-lg" style={{ width: '100%' }}>
              Submit Request
            </button>
          </form>
        </div>
      </section>
    </div>
  );
}
